import React, { useMemo } from 'react';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { File, ChevronLeft, ChevronRight } from 'lucide-react';
import { FileTreeNode } from './FileTreeNode';

interface SourceFile {
  filename: string;
  path?: string;
  file_size_bytes: number;
  artifact_type: string;
  artifact_id?: string | null;
  created_at?: string | null;
}

interface SourceFileTabsProps {
  sourceId: string;
  files: SourceFile[];
  currentTab: 'list' | 'tree' | 'stats';
  onTabChange: (tab: 'list' | 'tree' | 'stats') => void;
  fileListPage: number;
  onFileListPageChange: (page: number) => void;
  fileListPageSize: number;
  expandedTreePaths: Set<string>;
  onExpandedTreePathsChange: (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  buildFileTree: (files: SourceFile[]) => any[];
  formatFileSize: (bytes: number) => string;
  supportedExtensions: Set<string>;
  extensionDescriptions: Record<string, string>;
  fileExtensions: string[];
  source: {
    file_count?: number;
    total_size_bytes?: number;
    files_by_type?: Record<string, number>;
    artifact_ids?: string[];
    files?: SourceFile[];
  };
}

export function SourceFileTabs({
  sourceId,
  files,
  currentTab,
  onTabChange,
  fileListPage,
  onFileListPageChange,
  fileListPageSize,
  expandedTreePaths,
  onExpandedTreePathsChange,
  buildFileTree,
  formatFileSize,
  supportedExtensions,
  extensionDescriptions,
  fileExtensions,
  source,
}: SourceFileTabsProps) {
  const currentPage = fileListPage;
  const totalPages = Math.ceil(files.length / fileListPageSize);
  const paginatedFiles = useMemo(
    () => files.slice(currentPage * fileListPageSize, (currentPage + 1) * fileListPageSize),
    [files, currentPage, fileListPageSize]
  );

  const uploadedCount = useMemo(() => files.filter(f => f.artifact_id).length, [files]);
  const pendingCount = useMemo(() => files.filter(f => !f.artifact_id).length, [files]);

  return (
    <div className="mt-4 pt-4 border-t">
      {/* Tabs */}
      <div className="flex gap-2 border-b mb-4">
        <button
          onClick={() => onTabChange('list')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            currentTab === 'list'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          File List ({files.length})
        </button>
        <button
          onClick={() => onTabChange('tree')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            currentTab === 'tree'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          File Tree
        </button>
        <button
          onClick={() => onTabChange('stats')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            currentTab === 'stats'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Statistics
        </button>
      </div>

      {/* File List Tab */}
      {currentTab === 'list' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-sm font-semibold text-gray-700">
              All Files ({files.length})
              {totalPages > 1 && (
                <span className="text-xs font-normal text-gray-500 ml-2">
                  (Page {currentPage + 1} of {totalPages})
                </span>
              )}
            </h5>
            <div className="text-xs text-gray-500">
              {uploadedCount} uploaded, {pendingCount} pending
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
            <div className="divide-y divide-gray-200">
              {paginatedFiles.map((file, idx) => {
                const isUploaded = !!file.artifact_id;
                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 hover:bg-gray-50 transition-colors ${
                      !isUploaded ? 'opacity-75' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <File className={`h-4 w-4 flex-shrink-0 ${isUploaded ? 'text-green-600' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-900 truncate font-medium" title={file.filename}>
                            {file.filename}
                          </span>
                          {!isUploaded && (
                            <Badge variant="warning" className="text-xs">
                              Pending
                            </Badge>
                          )}
                          {isUploaded && (
                            <Badge variant="success" className="text-xs">
                              Uploaded
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="default" className="text-xs border border-gray-300">
                            {file.artifact_type}
                          </Badge>
                          {file.created_at && (
                            <span className="text-xs text-gray-500">
                              {new Date(file.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                      <span className="text-sm font-medium text-gray-700">
                        {formatFileSize(file.file_size_bytes)}
                      </span>
                      {isUploaded && file.artifact_id && (
                        <button
                          onClick={() => {
                            window.location.href = `#/artifacts/${file.artifact_id}`;
                          }}
                          className="text-xs px-2 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                          title="View artifact details"
                        >
                          View
                        </button>
                      )}
                      {!isUploaded && (
                        <span className="text-xs text-gray-400 italic">Not uploaded yet</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-xs text-gray-500">
                Showing {currentPage * fileListPageSize + 1}-{Math.min((currentPage + 1) * fileListPageSize, files.length)} of {files.length}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onFileListPageChange(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i;
                    } else if (currentPage < 2) {
                      pageNum = i;
                    } else if (currentPage > totalPages - 3) {
                      pageNum = totalPages - 5 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => onFileListPageChange(pageNum)}
                        className={`px-3 py-1 text-xs rounded ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onFileListPageChange(Math.min(totalPages - 1, currentPage + 1))}
                  disabled={currentPage >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* File Tree Tab */}
      {currentTab === 'tree' && (
        <div>
          <h5 className="text-sm font-semibold text-gray-700 mb-2">File locations in folder structure:</h5>
          <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto p-3 bg-gray-50">
            {(() => {
              const tree = buildFileTree(files);
              
              // Build set of all paths in tree recursively
              const buildAllPaths = (nodes: any[]): Set<string> => {
                const allPaths = new Set<string>();
                const collectPaths = (nodeList: any[]) => {
                  nodeList.forEach(node => {
                    if (node.path) allPaths.add(node.path);
                    if (node.name) allPaths.add(node.name);
                    if (node.children && node.children.length > 0) {
                      collectPaths(node.children);
                    }
                  });
                };
                collectPaths(nodes);
                return allPaths;
              };
              
              // Initialize with all paths expanded if not set
              let expandedPaths = expandedTreePaths || new Set<string>();
              if (expandedPaths.size === 0 && tree.length > 0) {
                const allPaths = buildAllPaths(tree);
                expandedPaths = allPaths;
                // Update state asynchronously to avoid render issues
                setTimeout(() => {
                  onExpandedTreePathsChange(allPaths);
                }, 0);
              }
              
              return tree.length > 0 ? (
                tree.map((node: any, idx: number) => (
                  <FileTreeNode
                    key={idx}
                    node={node}
                    level={0}
                    toggleHidePath={() => {}}
                    hiddenPaths={new Set()}
                    formatFileSize={formatFileSize}
                    supportedExtensions={supportedExtensions}
                    extensionDescriptions={extensionDescriptions}
                    selectedExtensions={fileExtensions}
                    expandedTreePaths={expandedPaths}
                    setExpandedTreePaths={onExpandedTreePathsChange}
                  />
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No files available</p>
              );
            })()}
          </div>
        </div>
      )}

      {/* Statistics Tab */}
      {currentTab === 'stats' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h5 className="text-sm font-semibold text-gray-700">File Statistics</h5>
            {source.artifact_ids && source.artifact_ids.length > 0 && (
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    window.location.href = `#/artifacts/${e.target.value}`;
                  }
                }}
                className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                defaultValue=""
              >
                <option value="">Quick View Artifact...</option>
                {source.artifact_ids.slice(0, 20).map((artifactId) => {
                  const file = source.files?.find(f => f.artifact_id === artifactId);
                  return (
                    <option key={artifactId} value={artifactId}>
                      {file?.filename || artifactId.substring(0, 8)}
                    </option>
                  );
                })}
              </select>
            )}
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
            <div>
              <div className="text-gray-500 text-xs mb-1">Total Files</div>
              <div className="font-bold text-lg text-gray-900">{source.file_count}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-1">Total Size</div>
              <div className="font-bold text-lg text-gray-900">
                {formatFileSize(source.total_size_bytes ?? 0)}
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-1">File Types</div>
              <div className="font-bold text-lg text-gray-900">
                {Object.keys(source.files_by_type || {}).length}
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-1">Uploaded</div>
              <div className="font-bold text-lg text-gray-900">
                {source.artifact_ids?.length ?? 0} / {source.file_count}
              </div>
            </div>
          </div>
          
          {/* Files by Type - Detailed */}
          {source.files_by_type && Object.keys(source.files_by_type).length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-semibold text-gray-700 mb-2">Files by Type</h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(source.files_by_type)
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .map(([type, count]) => {
                    const typeFiles = source.files?.filter(f => f.artifact_type === type.replace('.', '')) || [];
                    const typeSize = typeFiles.reduce((sum, f) => sum + f.file_size_bytes, 0);
                    return (
                      <div key={type} className="p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="default" className="text-xs font-semibold">
                            {type}
                          </Badge>
                          <span className="text-sm font-bold text-gray-900">{count as number}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatFileSize(typeSize)}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

